import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Typography } from '@material-ui/core';
import { palette } from '../../../settings/themes/colors';
import { FormattedMessage, useIntl } from 'react-intl';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

/**
 * HorizontalStackedBar
 *
 * Horizontal stacked bar chart (Recharts) supporting N stacked series with one color per series.
 *
 * Self-contained:
 *  - Computes the X-axis upper bound with a nice-ticks algorithm (1, 2, 2.5, 5, 10 × magnitude).
 *  - Picks bar size, category gap, row height and corner radius adaptively from data.length.
 *  - Always fills its parent's height (the parent must have a defined height).
 *  - Renders in-bar value labels and a total label next to the last series of every row.
 *  - Sizes the Y-axis width dynamically to fit the longest label in the dataset.
 *
 * Color contract: every series should declare `color`. When one or more series omit it, the
 * component fills the gap with a deterministic golden-angle HSL palette and emits a single
 * console.warn per instance. The render never throws.
 *
 * Props:
 *   data       Array<object>      Required. Rows. Each row should expose `displayLabel` for the Y axis.
 *   series     Array<SeriesSpec>  Required. Stacked series spec.
 *   unit       string             Optional. Suffix appended to in-bar labels and row totals (e.g. "%", "USD").
 *   onReady    func               Optional. Called once with the resolved chart config (ticks, maxTick,
 *                                 layout, seriesTotals, grandTotal, resolvedSeriesColors, etc).
 *
 * SeriesSpec: { key: string, stackId?: string, label?: any, color?: string | (entry, index) => string }
 */

const Y_AXIS_DATA_KEY = 'displayLabel';
const NAME_COL_WIDTH_MIN = 80;
const NAME_COL_WIDTH_MAX = 200;
const Y_AXIS_CHAR_WIDTH = 7;
const CHART_RIGHT_MARGIN = 55;
const COMPACT_THRESHOLD = 11;
const RADIUS_LARGE = 10;
const RADIUS_SMALL = 6;
const BAR_SIZE_LARGE = 32;
const BAR_SIZE_COMPACT = 18;
const GAP_LARGE = '30%';
const GAP_COMPACT = '14%';
const SEGMENT_LABEL_MIN_WIDTH = 18;
const TOTAL_LABEL_OFFSET = 6;
const SEGMENT_LABEL_FONT_SIZE = 10;
const TOTAL_LABEL_FONT_SIZE = 11;
const TOTAL_LABEL_COLOR = '#555555';
const Y_AXIS_TICK_COLOR = '#6D6D6D';
const GOLDEN_ANGLE_DEGREES = 137.508;
const GENERATED_COLOR_SATURATION = 65;
const GENERATED_COLOR_LIGHTNESS = 55;

const generateFallbackColor = index => {
  const hue = (index * GOLDEN_ANGLE_DEGREES) % 360;
  return `hsl(${hue}, ${GENERATED_COLOR_SATURATION}%, ${GENERATED_COLOR_LIGHTNESS}%)`;
};

const hasColor = series => series && series.color != null;

const computeTicks = (data, series) => {
  if (!data || data.length === 0) return [0, 1];
  const max = Math.max(...data.map(item => series.reduce((sum, s) => sum + (item[s.key] || 0), 0)));
  if (max <= 0) return [0, 1];
  if (max < 4) {
    const ticks = [0];
    for (let t = 1; t <= Math.ceil(max); t++) ticks.push(t);
    return ticks;
  }
  const rawStep = max / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / magnitude;
  let niceStep;
  if (norm <= 1.5) niceStep = 1;
  else if (norm <= 2) niceStep = 2;
  else if (norm <= 2.5) niceStep = 2.5;
  else if (norm <= 5) niceStep = 5;
  else niceStep = 10;
  const step = Math.max(1, Math.round(niceStep * magnitude));
  const maxRounded = Math.ceil(max / step) * step;
  const ticks = [0];
  for (let t = step; t <= maxRounded + 0.001; t += step) ticks.push(Math.round(t));
  return ticks;
};

const computeRowTotals = (data, series) =>
  series.map(s => ({
    key: s.key,
    label: s.label,
    color: s.color,
    total: (data || []).reduce((sum, row) => sum + (row[s.key] || 0), 0)
  }));

const computeLayout = count => {
  if (count <= COMPACT_THRESHOLD) {
    return { barSize: BAR_SIZE_LARGE, barCategoryGap: GAP_LARGE, baseRadius: RADIUS_SMALL };
  }
  return { barSize: BAR_SIZE_COMPACT, barCategoryGap: GAP_COMPACT, baseRadius: RADIUS_LARGE };
};

const useStyles = makeStyles({
  chartWrapper: {
    flex: 1,
    minHeight: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  emptyState: {
    flex: 1,
    minHeight: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  emptyText: {
    fontSize: 13,
    color: Y_AXIS_TICK_COLOR
  }
});

const computeYAxisWidth = data => {
  if (!data || data.length === 0) return NAME_COL_WIDTH_MIN;
  const maxLen = Math.max(...data.map(row => String(row[Y_AXIS_DATA_KEY] || '').length));
  const computed = maxLen * Y_AXIS_CHAR_WIDTH + 12;
  return Math.min(NAME_COL_WIDTH_MAX, Math.max(NAME_COL_WIDTH_MIN, computed));
};

const DefaultYAxisTick = ({ x, y, payload }) => {
  return (
    <text x={x - 4} y={y} dy="0.35em" fontSize={12} fill={Y_AXIS_TICK_COLOR} textAnchor="end">
      {String(payload.value)}
    </text>
  );
};

DefaultYAxisTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({ value: PropTypes.any })
};

DefaultYAxisTick.defaultProps = { x: 0, y: 0, payload: { value: '' } };

const computeRadius = (index, totalSeries, baseRadius) => {
  if (totalSeries <= 1) return [baseRadius, baseRadius, baseRadius, baseRadius];
  if (index === 0) return [baseRadius, 0, 0, baseRadius];
  if (index === totalSeries - 1) return [0, baseRadius, baseRadius, 0];
  return [0, 0, 0, 0];
};

const buildSegmentLabelRenderer = (formatNumber, unit) => {
  const SegmentLabel = ({ x, y, width, height, value }) => {
    if (!value || width < SEGMENT_LABEL_MIN_WIDTH) return null;
    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        dy="0.35em"
        fill="white"
        fontSize={SEGMENT_LABEL_FONT_SIZE}
        textAnchor="middle"
        style={{ pointerEvents: 'none' }}
      >
        {unit ? `${formatNumber(value)}${unit}` : formatNumber(value)}
      </text>
    );
  };

  SegmentLabel.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    value: PropTypes.number
  };

  SegmentLabel.defaultProps = { x: 0, y: 0, width: 0, height: 0, value: 0 };

  return SegmentLabel;
};

const buildTotalLabelRenderer = (formatNumber, getRowTotal, unit) => {
  const TotalLabel = ({ x, y, width, height, index }) => {
    const total = getRowTotal(index);
    return (
      <text
        x={x + width + TOTAL_LABEL_OFFSET}
        y={y + height / 2}
        dy="0.35em"
        fill={TOTAL_LABEL_COLOR}
        fontSize={TOTAL_LABEL_FONT_SIZE}
        textAnchor="start"
        style={{ pointerEvents: 'none' }}
      >
        {unit ? `${formatNumber(total)}${unit}` : formatNumber(total)}
      </text>
    );
  };

  TotalLabel.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    index: PropTypes.number
  };

  TotalLabel.defaultProps = { x: 0, y: 0, width: 0, height: 0, index: 0 };

  return TotalLabel;
};

const buildTooltip = (resolvedSeries, formatNumber, unit) => {
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div
        style={{
          background: 'white',
          border: `1px solid ${palette.platinum}`,
          borderRadius: 4,
          padding: '8px 12px',
          fontSize: 12,
          fontFamily: 'Nunito',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
        }}
      >
        {resolvedSeries.map(s => {
          const entry = payload.find(p => p.dataKey === s.key);
          if (!entry) return null;
          const display = unit ? `${formatNumber(entry.value)}${unit}` : formatNumber(entry.value);
          return (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <span style={{ color: palette.dimGray, fontWeight: 500 }}>{s.label || s.key}:</span>
              <span style={{ color: s.color, fontWeight: 700 }}>{display}</span>
            </div>
          );
        })}
      </div>
    );
  };
  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array
  };
  CustomTooltip.defaultProps = { active: false, payload: [] };
  return CustomTooltip;
};

export const HorizontalStackedBar = ({ data, series, unit, onReady }) => {
  const classes = useStyles();
  const intl = useIntl();
  const warnedRef = useRef(false);
  const readyRef = useRef(false);

  if (!series || series.length === 0 || !data || data.length === 0) {
    return (
      <div className={classes.emptyState}>
        <Typography className={classes.emptyText}>
          <FormattedMessage id="dashboard.noData" />
        </Typography>
      </div>
    );
  }

  const missingIndices = series.reduce((acc, s, idx) => (hasColor(s) ? acc : acc.concat(idx)), []);

  if (missingIndices.length > 0 && !warnedRef.current) {
    const generated = missingIndices.map(generateFallbackColor);
    // eslint-disable-next-line no-console
    console.warn(
      `[HorizontalStackedBar] ${missingIndices.length} of ${
        series.length
      } series are missing 'color' — generated fallback colors at indices [${missingIndices.join(', ')}] (${generated.join(', ')}).`
    );
    warnedRef.current = true;
  }

  const ticks = computeTicks(data, series);
  const maxTick = ticks[ticks.length - 1];
  const yAxisWidth = computeYAxisWidth(data);
  const layout = computeLayout(data.length);
  const { barSize, barCategoryGap, baseRadius } = layout;
  const formatNumber = value => intl.formatNumber(value);
  const getRowTotal = index => series.reduce((sum, s) => sum + (data[index] ? data[index][s.key] || 0 : 0), 0);
  const lastSeriesIndex = series.length - 1;
  const resolvedSeries = series.map((s, idx) => ({ ...s, color: hasColor(s) ? s.color : generateFallbackColor(idx) }));

  if (onReady && !readyRef.current) {
    readyRef.current = true;
    const seriesTotals = computeRowTotals(data, resolvedSeries);
    onReady({
      data,
      series: resolvedSeries,
      ticks,
      maxTick,
      layout,
      seriesTotals,
      grandTotal: seriesTotals.reduce((sum, st) => sum + st.total, 0),
      yAxisWidth,
      rowCount: data.length,
      seriesCount: series.length,
      hasMissingColors: missingIndices.length > 0,
      generatedColorIndices: missingIndices
    });
  }

  return (
    <div className={classes.chartWrapper}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: CHART_RIGHT_MARGIN, left: 0, bottom: 0 }}
          barSize={barSize}
          barCategoryGap={barCategoryGap}
        >
          <XAxis type="number" domain={[0, maxTick]} hide />
          <Tooltip content={buildTooltip(resolvedSeries, formatNumber, unit)} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          <YAxis
            type="category"
            dataKey={Y_AXIS_DATA_KEY}
            width={yAxisWidth}
            tick={<DefaultYAxisTick />}
            axisLine={false}
            tickLine={false}
          />
          {resolvedSeries.map((s, idx) => {
            const resolvedRadius = computeRadius(idx, resolvedSeries.length, baseRadius);
            const isLast = idx === lastSeriesIndex;
            const resolvedLabel = isLast
              ? buildTotalLabelRenderer(formatNumber, getRowTotal, unit)
              : buildSegmentLabelRenderer(formatNumber, unit);

            return (
              <Bar
                key={s.key}
                dataKey={s.key}
                stackId={s.stackId || 'a'}
                radius={resolvedRadius}
                label={resolvedLabel}
                isAnimationActive={false}
              >
                {data.map((entry, index) => (
                  <Cell key={index} fill={typeof s.color === 'function' ? s.color(entry, index) : s.color} />
                ))}
              </Bar>
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

HorizontalStackedBar.propTypes = {
  data: PropTypes.array.isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      stackId: PropTypes.string,
      label: PropTypes.any,
      color: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
    })
  ).isRequired,
  unit: PropTypes.string,
  onReady: PropTypes.func
};

HorizontalStackedBar.defaultProps = {
  unit: null,
  onReady: null
};
